import { EmptyState, ProfileIcon, Tab, TabItemProps } from '@bp/ui-components';
import { BpPage } from 'components/BpPage/BpPage';
import { HomeworkGrid } from 'components/Homeworks/components/HomeworkGrid/HomeworkGrid';
import { CallbackTab, CallbackTabs } from 'components/NavigationTabs/CallbackTabs';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { useIsDesktop } from 'hooks/useIsDesktop';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { useStackHandler } from 'hooks/useStackHandler';
import { useTranslation } from 'react-i18next';

export const HomeworksPage = () => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const children = pimAuthClaims.getChildProfiles();
  const organization = pimAuthClaims.getOrganization();

  const isDesktop = useIsDesktop();
  const stackHandler = useStackHandler();
  const permissions = usePermissionChecker();

  const isParent = permissions.isMemberWithChildren(organization);

  const tabs: TabItemProps[] = children.map((c) => ({
    title: c.firstName ?? '',
    value: c.uuid,
    content: <HomeworkGrid profileUuid={c.uuid} />,
  }));

  const callbackTabs: CallbackTab[] = children.map((c) => ({
    id: c.uuid,
    title: c.firstName ?? '',
    onClick: () => handleOpen(c.uuid, c.firstName ?? ''),
  }));

  function handleOpen(childUuid: string, name: string) {
    const id = `homework-grid-mobile-modal-${childUuid}`;
    stackHandler.push({
      id,
      title: t('homeworks.for', { name }),
      group: 'homework',
      onClose: () => stackHandler.closeById(id),
      component: <HomeworkGrid profileUuid={childUuid} />,
    });
  }

  return (
    <BpPage
      title={isParent ? t('homeworks.forChildren') : t('homeworks.forMe')}
      isForbidden={!permissions?.canViewHomeworks(organization)}
    >
      {!isParent ? (
        <HomeworkGrid profileUuid={pimAuthClaims.getProfile().uuid} />
      ) : children.length === 0 ? (
        <EmptyState icon={<ProfileIcon />} title={t('children.noChildren')} forcedHeight='40vh' />
      ) : children.length === 1 ? (
        <HomeworkGrid profileUuid={children[0].uuid} />
      ) : isDesktop ? (
        <Tab variant='large' tabs={tabs} />
      ) : (
        <>
          <div className='mb-5'>{t('homeworks.forWhichChild')}</div>
          <CallbackTabs tabs={callbackTabs} />
        </>
      )}
    </BpPage>
  );
};
